export const CA_CERTIFICATE_FIELDS: readonly string[] = [
  "caCertificate",
  "tlsCaCertificate",
];

export const INVALID_CA_CERTIFICATE_MESSAGE =
  "Upload a valid PEM (Base-64 encoded X.509) certificate. Binary DER certificates aren’t supported.";

export function certificatePemBlocks(value: string): string[] | null {
  const begin = "-----BEGIN CERTIFICATE-----";
  const end = "-----END CERTIFICATE-----";
  if (/-----BEGIN (?!CERTIFICATE-----)/.test(value)) return null;
  const [, ...parts] = value.split(begin);
  if (parts.length === 0) return null;
  const certificates: string[] = [];
  for (const part of parts) {
    const endIndex = part.indexOf(end);
    if (endIndex === -1) return null;
    certificates.push(`${begin}${part.slice(0, endIndex + end.length)}`);
  }
  return certificates;
}

export function trimmedCaCertificate(
  value: string | null | undefined
): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function pemBlockDer(block: string): Uint8Array<ArrayBuffer> | null {
  const body = block
    .replace("-----BEGIN CERTIFICATE-----", "")
    .replace("-----END CERTIFICATE-----", "")
    .replace(/\s+/g, "");
  if (!body) return null;
  try {
    return Uint8Array.from(atob(body), (char) => char.charCodeAt(0));
  } catch {
    return null;
  }
}

function bundleBlocks(bundle: string | undefined): string[] {
  const trimmed = trimmedCaCertificate(bundle);
  return (trimmed && certificatePemBlocks(trimmed)) || [];
}

function derKey(block: string): string | null {
  const der = pemBlockDer(block);
  return (
    der &&
    Array.from(der, (byte) => byte.toString(16).padStart(2, "0")).join("")
  );
}

export function certificateMembership(
  bundle: string | undefined
): (pem: string) => boolean {
  const keys = new Set(bundleBlocks(bundle).map(derKey));
  return (pem) => {
    const key = derKey(pem);
    return key !== null && keys.has(key);
  };
}

export function withTrustedCertificate(
  bundle: string | undefined,
  pem: string
): string {
  if (!bundle || bundleBlocks(bundle).length === 0) return pem;
  if (certificateMembership(bundle)(pem)) return bundle;
  return `${bundle.trimEnd()}\n${pem}`;
}

export function withoutTrustedCertificate(
  bundle: string | undefined,
  pem: string
): string | null {
  const isPem = certificateMembership(pem);
  const kept = bundleBlocks(bundle).filter((block) => !isPem(block));
  return kept.length > 0 ? kept.join("\n") : null;
}

export async function certificateFingerprints(
  value: string | null | undefined
): Promise<string[]> {
  const unwrapped = trimmedCaCertificate(value);
  const blocks = unwrapped ? certificatePemBlocks(unwrapped) : null;
  const fingerprints: string[] = [];
  for (const block of blocks ?? []) {
    const der = pemBlockDer(block);
    if (!der) continue;
    const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", der));
    fingerprints.push(
      Array.from(digest, (byte) => byte.toString(16).padStart(2, "0"))
        .join(":")
        .toUpperCase()
    );
  }
  return fingerprints;
}

export async function certificateAuditMetadata(
  beforeConfiguration: Record<string, string>,
  afterConfiguration: Record<string, string>
): Promise<{
  caCertificate?: { from: string[]; to: string[] };
  tlsCaCertificate?: { from: string[]; to: string[] };
}> {
  const metadata: Record<string, { from: string[]; to: string[] }> = {};
  for (const field of CA_CERTIFICATE_FIELDS) {
    const previous = trimmedCaCertificate(beforeConfiguration[field]);
    const next = trimmedCaCertificate(afterConfiguration[field]);
    if (previous === next) continue;
    metadata[field] = {
      from: await certificateFingerprints(previous),
      to: await certificateFingerprints(next),
    };
  }
  return metadata;
}

export function derToPem(base64Der: string): string {
  const lines = base64Der.replace(/\s+/g, "").match(/.{1,64}/g) ?? [];
  return [
    "-----BEGIN CERTIFICATE-----",
    ...lines,
    "-----END CERTIFICATE-----",
    "",
  ].join("\n");
}
